import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, fmtErr, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = { nama: "", pelaksanaan: "", image: [] };

export default function ProgramTab() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/programs").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (item) => {
    setForm(item ? { nama: item.nama, pelaksanaan: item.pelaksanaan, image: item.image ? [item.image] : [] } : empty);
    setDlg(item || "new");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { nama: form.nama, pelaksanaan: form.pelaksanaan, image: form.image[0] || "" };
    try {
      if (dlg === "new") await api.post("/programs", payload);
      else await api.put(`/programs/${dlg.id}`, payload);
      toast.success(t("common.saved"));
      setDlg(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/programs/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="program-tab">
      <Button onClick={() => open(null)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 mb-6" data-testid="program-add-button">
        <Plus className="h-4 w-4 mr-2" /> {t("common.add")}
      </Button>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm overflow-hidden" data-testid={`program-item-${p.id}`}>
            {p.image && <img src={imgUrl(p.image)} alt={p.nama} className="aspect-video w-full object-cover" />}
            <div className="p-4">
              <h4 className="font-semibold text-emerald-950 text-sm">{p.nama}</h4>
              <p className="text-xs text-slate-500 mt-1">{p.pelaksanaan}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => open(p)} data-testid={`program-edit-${p.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t("common.edit")}</Button>
                <Button size="sm" variant="destructive" onClick={() => remove(p.id)} data-testid={`program-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t("common.delete")}</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-950">{t("dash.program")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="program-form">
            <div>
              <Label className="text-sm font-medium text-slate-700">Nama Program / Kegiatan</Label>
              <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required className="mt-1.5" data-testid="program-form-nama" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Pelaksanaan Kegiatan</Label>
              <Textarea value={form.pelaksanaan} onChange={(e) => setForm({ ...form, pelaksanaan: e.target.value })} required rows={3} className="mt-1.5" data-testid="program-form-pelaksanaan" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Thumbnail</Label>
              <div className="mt-1.5"><ImagePicker values={form.image} onChange={(image) => setForm({ ...form, image })} max={1} testId="program-image-picker" /></div>
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="program-form-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
